/**
 * RepairAgentStateBadge — RA-AGENT-STATE badge, shared by RepairDetailPage.tsx
 * and RepairsPage.tsx's per-row badge so both render the same component
 * instead of duplicating the state->label/variant map.
 *
 * Both AGENT_STATE_CONFIG and RepairAgentStateBadge are re-exported: nothing
 * needs the config map directly today, exported anyway for the same reason
 * RepairComposer exports its pure helpers (partyOptionValue/partyOptionLabel).
 */

import { Badge } from '../ui/badge';
import type { BadgeProps } from '../ui/badge';

// RA-AGENT-STATE — label per state; 'active' renders no badge (matches existing pages'
// convention of omitting a badge for the non-actionable default state). Precedence (disabled ->
// pending_triage -> escalation_pending -> paused -> awaiting_vendor -> active) is enforced
// entirely server-side by repair-status.ts's deriveAgentState() — this map is
// presentation-only and never re-derives or re-orders it.
export const AGENT_STATE_CONFIG: Record<string, { label: string; variant: NonNullable<BadgeProps['variant']> } | null> = {
  disabled: { label: 'AI disabled — landlord policy', variant: 'secondary' },
  pending_triage: { label: 'Awaiting AI categorization', variant: 'warning' },
  escalation_pending: { label: 'Waiting on your answer', variant: 'warning' },
  // RA-AGENT-STATE-PAUSED — a landlord took over (e.g. sent a direct party message via
  // RepairComposer) with no open escalation. This is the feature's single most
  // safety-critical status indicator (it tells the landlord the agent stopped working the
  // case); contrast is owned by the Badge variant tokens.
  paused: { label: "Paused — you're handling it", variant: 'warning' },
  awaiting_vendor: { label: 'Waiting on vendor', variant: 'info' },
  active: null,
};

export function RepairAgentStateBadge({ agentState }: { agentState: string | undefined }) {
  if (!agentState) return null;
  const cfg = AGENT_STATE_CONFIG[agentState];
  if (!cfg) return null;
  return (
    <Badge
      data-testid="repair-detail-agent-state-badge"
      className="rounded-full font-bold"
      variant={cfg.variant}
    >
      {cfg.label}
    </Badge>
  );
}

export default RepairAgentStateBadge;
